import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, Edit, Copy, Archive, Play, Trash2, FileText } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getIdToken } from "@/lib/auth";
import FormDesignerModal from "./FormDesignerModal";
import FormViewModal from "./FormViewModal";

interface CustomFormQuestion {
  id: string;
  text: string;
  type: 'OPEN_TEXT' | 'MULTIPLE_CHOICE' | 'RATING' | 'YES_NO';
  isRequired: boolean;
  order: number;
  options: Array<{
    id: string;
    text: string;
    value: string;
    order: number;
  }>;
}

interface CustomForm {
  id: string;
  name: string;
  productId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
  };
  questions: CustomFormQuestion[];
}

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Get Firebase ID token
const getAuthToken = async () => {
  return getIdToken();
};

// Helper function to add auth headers
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

const CustomFormsPage = () => {
  const [forms, setForms] = useState<CustomForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormDesignerOpen, setIsFormDesignerOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<CustomForm | null>(null);
  const [designerMode, setDesignerMode] = useState<'create' | 'edit'>('create');
  const [isFormViewOpen, setIsFormViewOpen] = useState(false);
  const [viewingForm, setViewingForm] = useState<CustomForm | null>(null);
  const { toast } = useToast();

  const fetchForms = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/custom-forms`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch custom forms');
      }
      
      const data = await response.json();
      setForms(data);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast({
        title: "Error",
        description: "Failed to load custom forms",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleStatusUpdate = async (formId: string, newStatus: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/custom-forms/${formId}/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update form status');
      }

      toast({
        title: "Success",
        description: `Form status updated to ${newStatus.toLowerCase()}`,
      });

      // Refresh the forms list
      fetchForms();
    } catch (error) {
      console.error('Error updating form status:', error);
      toast({
        title: "Error",
        description: "Failed to update form status",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async (formId: string) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/custom-forms/${formId}/duplicate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate form');
      }

      toast({
        title: "Success",
        description: "Form duplicated successfully",
      });

      // Refresh the forms list
      fetchForms();
    } catch (error) {
      console.error('Error duplicating form:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate form",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (formId: string) => {
    if (!confirm('Are you sure you want to delete this form? This will permanently delete the form and ALL responses and answers submitted by users. This action cannot be undone.')) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/custom-forms/${formId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete form');
      }

      toast({
        title: "Success",
        description: "Form deleted successfully",
      });

      // Refresh the forms list
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      toast({
        title: "Error",
        description: "Failed to delete form",
        variant: "destructive",
      });
    }
  };

  const handleCreateForm = () => {
    setDesignerMode('create');
    setEditingForm(null);
    setIsFormDesignerOpen(true);
  };

  const handleEditForm = (form: CustomForm) => {
    setDesignerMode('edit');
    setEditingForm(form);
    setIsFormDesignerOpen(true);
  };

  const handleFormDesignerClose = () => {
    setIsFormDesignerOpen(false);
    setEditingForm(null);
  };

  const handleFormDesignerSave = () => {
    fetchForms(); // Refresh the forms list
  };

  const handleViewForm = (form: CustomForm) => {
    setViewingForm(form);
    setIsFormViewOpen(true);
  };

  const handleFormViewClose = () => {
    setIsFormViewOpen(false);
    setViewingForm(null);
  };



  const getStatusBadge = (status: string) => {
    const variants = {
      DRAFT: "secondary",
      ACTIVE: "default",
      ARCHIVED: "destructive",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {status.toLowerCase()}
      </Badge>
    );
  };

  const getStatusActions = (form: CustomForm) => {
    const actions = [];

    // View action - available for all statuses
    actions.push(
      <Button
        key="view"
        variant="ghost"
        size="sm"
        onClick={() => handleViewForm(form)}
        title="View Form"
      >
        <Eye className="h-4 w-4" />
      </Button>
    );

    // Edit action - only for DRAFT forms
    if (form.status === 'DRAFT') {
      actions.push(
        <Button
          key="edit"
          variant="ghost"
          size="sm"
          onClick={() => handleEditForm(form)}
          title="Edit Form"
        >
          <Edit className="h-4 w-4" />
        </Button>
      );
    }

    // Activate action - only for DRAFT forms
    if (form.status === 'DRAFT') {
      actions.push(
        <Button
          key="activate"
          variant="ghost"
          size="sm"
          onClick={() => handleStatusUpdate(form.id, 'ACTIVE')}
          title="Activate Form"
        >
          <Play className="h-4 w-4" />
        </Button>
      );
    }

    // Archive action - only for ACTIVE forms
    if (form.status === 'ACTIVE') {
      actions.push(
        <Button
          key="archive"
          variant="ghost"
          size="sm"
          onClick={() => handleStatusUpdate(form.id, 'ARCHIVED')}
          title="Archive Form"
        >
          <Archive className="h-4 w-4" />
        </Button>
      );
    }



    // Duplicate action - available for all statuses
    actions.push(
      <Button
        key="duplicate"
        variant="ghost"
        size="sm"
        onClick={() => handleDuplicate(form.id)}
        title="Duplicate Form"
      >
        <Copy className="h-4 w-4" />
      </Button>
    );

    // Delete action - only for DRAFT and ARCHIVED forms
    if (form.status !== 'ACTIVE') {
      actions.push(
        <Button
          key="delete"
          variant="ghost"
          size="sm"
          onClick={() => handleDelete(form.id)}
          title="Delete Form"
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      );
    }

    return actions;
  };

  // Group forms by product
  const formsByProduct = forms.reduce((acc, form) => {
    const productName = form.product.name;
    if (!acc[productName]) {
      acc[productName] = [];
    }
    acc[productName].push(form);
    return acc;
  }, {} as Record<string, CustomForm[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading custom forms...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground">
            Manage your custom feedback forms
          </p>
        </div>
        <Button onClick={handleCreateForm}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Form
        </Button>
      </div>

      {Object.keys(formsByProduct).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No custom forms yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first custom form to start collecting structured feedback
            </p>
            <Button onClick={handleCreateForm}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        Object.entries(formsByProduct).map(([productName, productForms]) => (
          <div key={productName} className="space-y-4">
            <h2 className="text-xl font-semibold">{productName}</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {productForms.map((form) => (
                <Card key={form.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{form.name}</CardTitle>
                      {getStatusBadge(form.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                      {form.questions.length} question{form.questions.length !== 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-muted-foreground">
                        Created {new Date(form.createdAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        {getStatusActions(form)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Form Designer Modal */}
      <FormDesignerModal
        isOpen={isFormDesignerOpen}
        onClose={handleFormDesignerClose}
        onSave={handleFormDesignerSave}
        form={editingForm}
        mode={designerMode}
      />

      {/* Form View Modal */}
      <FormViewModal
        isOpen={isFormViewOpen}
        onClose={handleFormViewClose}
        form={viewingForm}
      />


    </div>
  );
};

export default CustomFormsPage; 